/**
 * RequestDetailModal — controlo total do admin sobre um trabalho.
 * Morada, foto, preços, dias a decorrer, cartões expansíveis de
 * cliente/trabalhador e pagamentos faseados (do valor em escrow).
 */
import React, { useEffect, useState } from 'react';
import { getRequestFullDetail, addPayout, fileUrl } from '../utils/api';
import LoadingSpinner from './common/LoadingSpinner';
import {
  X, MapPin, User, Briefcase, Calendar, Clock, Phone, Mail,
  Star, ShieldCheck, Banknote, ChevronDown, ChevronUp, Camera
} from 'lucide-react';

const kz = (v) => `Kz ${Number(v || 0).toLocaleString('pt-PT')}`;

const PAYMENT_LABELS = {
  unpaid:   ['bg-gray-500/20 text-gray-400', 'Por pagar'],
  paid:     ['bg-blue-500/20 text-blue-400', '🔒 Protegido (pago)'],
  partially_released: ['bg-purple-500/20 text-purple-400', '🔄 Libertação parcial'],
  released: ['bg-green-500/20 text-green-400', 'Libertado'],
  held:     ['bg-amber-500/20 text-amber-400', 'Cativo (mediação)'],
  disputed: ['bg-red-500/20 text-red-400', 'Em mediação'],
};

const PersonCard = ({ title, icon: Icon, person, extra }) => {
  const [open, setOpen] = useState(false);
  if (!person) return (
    <div className="bg-gray-700/40 border border-gray-700 rounded-lg p-3 text-gray-500 text-sm">
      {title}: ainda não atribuído
    </div>
  );
  return (
    <div className="bg-gray-700/40 border border-gray-700 rounded-lg">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-700/60 transition-colors rounded-lg">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold">
          {person.full_name?.[0]}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">
            {person.full_name}
            {person.verification_status === 'verified' && (
              <span className="ml-2 text-green-400 text-xs">Verificado</span>
            )}
          </p>
          <p className="text-gray-400 text-xs flex items-center gap-1">
            <Icon className="w-3 h-3" /> {title}
          </p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 text-sm space-y-1 border-t border-gray-700 pt-2">
          <p className="text-gray-300 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-teal-400" /> {person.email}</p>
          <p className="text-gray-300 flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-teal-400" /> {person.phone_number || 'N/A'}</p>
          <p className="text-gray-300 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-teal-400" />
            Na Dozy desde {person.created_at ? new Date(person.created_at).toLocaleDateString('pt-PT') : '—'}
          </p>
          {extra}
        </div>
      )}
    </div>
  );
};

const RequestDetailModal = ({ requestId, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    getRequestFullDetail(requestId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [requestId]);

  const handlePayout = async () => {
    setError('');
    if (!payAmount || Number(payAmount) <= 0) { setError('Indique o valor da fase (Kz).'); return; }
    setBusy(true);
    try {
      await addPayout(requestId, Number(payAmount), payNote);
      setPayAmount(''); setPayNote('');
      load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao registar pagamento');
    } finally {
      setBusy(false);
    }
  };

  const r = detail?.request;
  const [payCls, payLabel] = PAYMENT_LABELS[r?.payment_status] || PAYMENT_LABELS.unpaid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
      onClick={onClose}>
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        {loading ? (
          <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
        ) : !detail ? (
          <div className="p-8 text-center text-gray-400">Erro ao carregar o detalhe.</div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-5 flex items-start justify-between gap-3 z-10">
              <div>
                <h2 className="text-white text-lg font-bold">
                  #{r.request_id} — {r.service_type}
                </h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${payCls}`}>{payLabel}</span>
                  <span className="text-gray-400 text-xs">{r.status}</span>
                  {r.assigned_at && (
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {detail.request.days_running ?? 0} dia(s) a decorrer
                    </span>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Info do trabalho */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <p className="text-gray-300 flex items-start gap-2 sm:col-span-2">
                  <MapPin className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span><span className="text-gray-500">Morada:</span> {r.address || 'Não indicada'}</span>
                </p>
                {r.description && (
                  <p className="text-gray-300 sm:col-span-2"><span className="text-gray-500">Descrição:</span> {r.description}</p>
                )}
                <p className="text-gray-300"><span className="text-gray-500">Tipo:</span> {r.request_type === 'quotation' ? '📄 Orçamento' : '🛠️ Pedido directo'}</p>
                <p className="text-gray-300"><span className="text-gray-500">Criado:</span> {new Date(r.created_at).toLocaleString('pt-PT')}</p>
                <p className="text-gray-300"><span className="text-gray-500">Preço oferecido:</span> {r.proposed_price ? kz(r.proposed_price) : '—'}</p>
                <p className="text-teal-400 font-semibold"><span className="text-gray-500 font-normal">Preço acordado:</span> {r.agreed_price ? kz(r.agreed_price) : '—'}</p>
                {Number(r.released_percent) > 0 && (
                  <p className="text-purple-300"><span className="text-gray-500">Libertado (faseado):</span> {Number(r.released_percent)}% ({kz((r.agreed_price || 0) * Number(r.released_percent) / 100)})</p>
                )}
                {Number(r.pending_release_percent) > 0 && (
                  <p className="text-amber-300"><span className="text-gray-500">Parcela pendente:</span> profissional pede até {Number(r.pending_release_percent)}%</p>
                )}
                {r.settlement_percent != null && (
                  <p className="text-amber-400 sm:col-span-2">Decisão admin: profissional recebeu {r.settlement_percent}%</p>
                )}
              </div>

              {r.photo && (
                <a href={fileUrl(r.photo)} target="_blank" rel="noreferrer" className="block">
                  <p className="text-gray-500 text-xs mb-1 flex items-center gap-1"><Camera className="w-3 h-3" /> Foto anexada pelo cliente</p>
                  <img src={fileUrl(r.photo)} alt="Anexo" className="w-full max-h-52 object-cover rounded-lg border border-gray-700" />
                </a>
              )}

              {/* Pessoas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PersonCard title="Cliente" icon={User} person={detail.client}
                  extra={<p className="text-gray-400 text-xs">📋 {detail.client?.total_requests || 0} pedidos na Dozy</p>} />
                <PersonCard title="Profissional" icon={Briefcase} person={detail.worker}
                  extra={detail.worker && (
                    <>
                      <p className="text-gray-300 flex items-center gap-2">
                        <Star className="w-3.5 h-3.5 text-yellow-400" />
                        {detail.worker.average_rating ? `${Number(detail.worker.average_rating).toFixed(1)}/5` : 'Sem avaliações'}
                        <span className="text-gray-500">({detail.worker.total_reviews || 0})</span>
                      </p>
                      <p className="text-gray-300 flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-teal-400" /> {detail.worker.profession || 'Sem profissão registada'}
                      </p>
                      <p className="text-gray-400 text-xs">🛠️ {detail.worker.completed_jobs || 0} trabalhos concluídos</p>
                    </>
                  )} />
              </div>

              {/* Candidaturas */}
              {detail.applications.length > 0 && (
                <div>
                  <p className="text-gray-400 text-xs font-semibold uppercase mb-2">Propostas ({detail.applications.length})</p>
                  <div className="space-y-1">
                    {detail.applications.map(a => (
                      <div key={a.application_id} className="flex items-center justify-between bg-gray-700/40 rounded-lg px-3 py-2 text-sm">
                        <span className="text-gray-300">{a.full_name} {a.status === 'chosen' && <span className="text-green-400 text-xs">✓ escolhido</span>}</span>
                        <span className="text-teal-400 font-medium">{kz(a.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pagamentos faseados */}
              <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                <p className="text-white font-semibold flex items-center gap-2 mb-1">
                  <Banknote className="w-4 h-4 text-green-400" /> Pagamentos faseados ao profissional
                </p>
                <p className="text-gray-400 text-xs mb-3">
                  Pago: <span className="text-green-400">{kz(detail.paid_out)}</span> •
                  Restante protegido: <span className="text-amber-400">{kz(detail.remaining)}</span> •
                  Total acordado: {kz(r.agreed_price)}
                </p>

                {detail.payouts.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {detail.payouts.map(p => (
                      <div key={p.payout_id} className="flex items-center justify-between text-sm bg-gray-800 rounded px-3 py-1.5">
                        <span className="text-gray-300">{p.note || 'Fase'} <span className="text-gray-500 text-xs">• {new Date(p.created_at).toLocaleString('pt-PT')}</span></span>
                        <span className="text-green-400 font-medium">{kz(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {['paid', 'held', 'released'].includes(r.payment_status) && detail.remaining > 0 ? (
                  <div className="flex gap-2 flex-wrap items-start">
                    <input type="number" min="1" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                      placeholder={`Valor (máx ${detail.remaining})`}
                      className="flex-1 min-w-[120px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    <input value={payNote} onChange={(e) => setPayNote(e.target.value)}
                      placeholder="Nota (ex: 50% início da obra)"
                      className="flex-[2] min-w-[160px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    <button onClick={handlePayout} disabled={busy}
                      className="px-4 py-2 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20
                                 hover:bg-green-500/20 text-sm font-medium disabled:opacity-50">
                      {busy ? '...' : '💸 Pagar Fase'}
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs">
                    {r.payment_status === 'unpaid'
                      ? 'O cliente ainda não pagou — sem valor protegido.'
                      : 'Valor totalmente pago ao profissional.'}
                  </p>
                )}
                {error && <p className="text-red-400 text-xs mt-2">⚠️ {error}</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestDetailModal;
